import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, rest } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryClient';
import type { BlogPost } from '@/types/database';

interface UseBlogOptions {
  category?: string;
  page?: number;
  limit?: number;
  includeUnpublished?: boolean;
}

export function useBlogPosts(options?: UseBlogOptions) {
  const { category, page = 1, limit = 10, includeUnpublished = false } = options || {};

  return useQuery({
    queryKey: queryKeys.blog.list({ category, page }),
    queryFn: async () => {
      let query = rest.from<BlogPost>('blog_posts')
        .select('*')
        .order('published_at', { ascending: false });

      if (!includeUnpublished) {
        query = query.eq('status', 'published');
      }

      if (category) {
        query = query.eq('category', category);
      }

      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        posts: data as BlogPost[],
        total: count || 0,
        totalPages: Math.ceil((count || 0) / limit),
        currentPage: page,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: queryKeys.blog.detail(slug),
    queryFn: async () => {
      const { data, error } = await rest.from<BlogPost>('blog_posts')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as BlogPost;
    },
    enabled: !!slug,
  });
}

export function useCreateBlogPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (post: Omit<BlogPost, 'id' | 'created_at' | 'updated_at' | 'views_count'>) => {
      const { data, error } = await supabase
        .from('blog_posts')
        .insert(post as any)
        .select()
        .single();

      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blog.all });
    },
  });
}

export function useUpdateBlogPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<BlogPost> & { id: string }) => {
      const { data, error } = await supabase
        .from('blog_posts')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blog.all });
      if (data?.slug) {
        queryClient.invalidateQueries({ queryKey: queryKeys.blog.detail(data.slug) });
      }
    },
  });
}

export function useDeleteBlogPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blog.all });
    },
  });
}

export function useBlogCategories() {
  return useQuery({
    queryKey: ['blogCategories'],
    queryFn: async () => {
      const { data, error } = await rest.from<BlogPost>('blog_posts')
        .select('category')
        .eq('status', 'published');

      if (error) throw error;

      const categories = [...new Set((data || []).map((p) => p.category).filter(Boolean))] as string[];
      return categories;
    },
  });
}
